/**
 * LembreteTeste — tela 6 do funil Premium: quando avisar antes do teste acabar.
 *
 * Fonte: handoffAnuncios/PROMPT-claude-code.md § 6 e Monetizacao.dc.html (1f).
 *
 * Datas REAIS: fim = hoje + trialDias; as opções são 2 e 3 dias antes do fim
 * ("4 de out."). Três estados:
 *   inicial     nada escolhido, VITA sino 260, CTA desabilitado;
 *   selecionado título "Fechado! A gente te avisa {n} dias antes…", VITA
 *               sino-joinha, card com check, CTA amarelo (haptic selection);
 *   negada      o pai devolveu { permissaoNegada: true } → banner danger com
 *               "Ativar notificações" (onAbrirAjustes), VITA 200 a 70%. A escolha
 *               fica; o CTA segue habilitado e um novo toque chama onEscolher.
 *
 * CTA: `const r = await onEscolher(n)`. Sem `permissaoNegada` o pai avança.
 */

import React, { useMemo, useState } from 'react';
import haptics from '../../design/feedback/haptics';
import cx from '../../design/cx';
import PainelPremium from './PainelPremium';
import PremiumTopBar from './PremiumTopBar';
import vitaSino from '../../Assets/images/vita/vita-sino.webp';
import vitaSinoJoinha from '../../Assets/images/vita/vita-sino-joinha.webp';
import s from './LembreteTeste.module.scss';

const OPCOES = [2, 3];

/** "4 de out." — dia + mês curto pt-BR, sempre com um ponto só. */
function dataCurta(data) {
  const mes = data.toLocaleDateString('pt-BR', { month: 'short' }).replace(/\./g, '');
  return `${data.getDate()} de ${mes}.`;
}

function opcoesDeLembrete(trialDias) {
  const fim = new Date();
  fim.setHours(12, 0, 0, 0);
  fim.setDate(fim.getDate() + trialDias);
  return OPCOES.filter((n) => n < trialDias).map((n) => {
    const d = new Date(fim);
    d.setDate(d.getDate() - n);
    return { n, data: dataCurta(d) };
  });
}

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export default function LembreteTeste({ trialDias = 7, onEscolher, onAbrirAjustes, onFechar, onVoltar }) {
  const opcoes = useMemo(() => opcoesDeLembrete(trialDias), [trialDias]);
  const [escolha, setEscolha] = useState(null);
  const [negada, setNegada] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const escolher = (n) => {
    if (n !== escolha) haptics.selection();
    setEscolha(n);
  };

  const confirmar = async () => {
    if (escolha == null || enviando) return;
    haptics.light();
    setEnviando(true);
    try {
      const r = await onEscolher?.(escolha);
      if (r && r.permissaoNegada) {
        haptics.warning();
        setNegada(true);
      }
    } catch (e) {
      // Falha do pai não pode travar a tela: o CTA volta e dá para tentar de novo.
      console.warn('[LembreteTeste] onEscolher falhou:', e);
    } finally {
      setEnviando(false);
    }
  };

  const rotuloCta = 'Assinar Turbo por R$0,00';

  const titulo =
    escolha == null ? (
      'Quando você quer um lembrete antes do teste acabar?'
    ) : (
      <>
        Fechado! A gente te avisa <b className={s.destaque}>{escolha} dias</b> antes do teste acabar.
      </>
    );

  const imagem = escolha != null && !negada ? vitaSinoJoinha : vitaSino;

  return (
    <PainelPremium rotulo="Lembrete do fim do teste" brilho="centro" onFechar={onFechar} onVoltar={onVoltar}>
      <PremiumTopBar onFechar={onFechar} onVoltar={onVoltar} />

      <div className={s.corpo}>
        <h1 className={s.titulo} aria-live="polite">{titulo}</h1>

        <img
          key={imagem}
          className={cx(s.vita, negada && s.vitaNegada)}
          src={imagem}
          alt=""
        />

        {negada && (
          <div className={s.banner} role="alert">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={s.bannerIcone}>
              <path d="M8.7 3A6 6 0 0 1 18 8c0 7 3 9 3 9H3s3-2 3-9a6 6 0 0 1 .7-2.8M10.3 21a1.94 1.94 0 0 0 3.4 0M2 2l20 20" />
            </svg>
            <span className={s.bannerTexto}>Notificações desligadas. Sem elas, o lembrete não chega.</span>
            <button
              type="button"
              className={s.chip}
              onClick={() => {
                haptics.light();
                onAbrirAjustes?.();
              }}
            >
              Ativar notificações
            </button>
          </div>
        )}

        <div className={s.opcoes} role="radiogroup" aria-label="Quando avisar">
          {opcoes.map(({ n, data }) => {
            const ativo = escolha === n;
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={ativo}
                className={cx(s.opcao, ativo && s.opcaoAtiva)}
                onClick={() => escolher(n)}
              >
                <span className={s.opcaoNome}>
                  {ativo && (
                    <span className={s.check}>
                      <Check />
                    </span>
                  )}
                  {n} dias antes
                </span>
                <span className={s.opcaoData}>{data}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={s.rodape}>
        <p className={s.nota}>Fácil de cancelar, sem multa nem taxa.</p>
        <button
          type="button"
          className={s.cta}
          disabled={escolha == null}
          aria-busy={enviando || undefined}
          onClick={confirmar}
        >
          {enviando && <span className={s.spinner} aria-hidden="true" />}
          {rotuloCta}
        </button>
      </div>
    </PainelPremium>
  );
}
